import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

const Popover = PopoverPrimitive.Root

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, forceMount, ...props }, ref) => (
  <PopoverPrimitive.Portal forceMount={forceMount}>
    <PopoverPrimitive.Content
      ref={ref}
      forceMount={forceMount}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

// 支持hover触发的Popover组件
interface HoverPopoverProps {
  children: React.ReactNode
  content: React.ReactNode
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  sideOffset?: number
  className?: string
  contentClassName?: string
  delayDuration?: number
}

const HoverPopover = React.forwardRef<
  HTMLDivElement,
  HoverPopoverProps
>(({ 
  children, 
  content, 
  side = "right", 
  align = "start", 
  sideOffset = 8,
  className,
  contentClassName,
  delayDuration = 200,
  ...props 
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const enterTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMouseEnter = React.useCallback(() => {
    // 清除离开的定时器
    if (leaveTimeoutRef.current !== null) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
    
    // 设置进入的定时器
    if (!open) {
      enterTimeoutRef.current = setTimeout(() => {
        setOpen(true)
      }, delayDuration)
    }
  }, [delayDuration, open])

  const handleMouseLeave = React.useCallback(() => {
    // 清除进入的定时器
    if (enterTimeoutRef.current !== null) {
      clearTimeout(enterTimeoutRef.current)
      enterTimeoutRef.current = null
    }
    
    // 设置离开的定时器
    leaveTimeoutRef.current = setTimeout(() => {
      setOpen(false)
    }, 150)
  }, [])

  React.useEffect(() => {
    return () => {
      if (enterTimeoutRef.current !== null) {
        clearTimeout(enterTimeoutRef.current)
      }
      if (leaveTimeoutRef.current !== null) {
        clearTimeout(leaveTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          ref={ref}
          className={className}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...props}
        >
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={contentClassName}
        onMouseEnter={() => {
          // 鼠标进入popover内容时，清除离开定时器
          if (leaveTimeoutRef.current !== null) {
            clearTimeout(leaveTimeoutRef.current)
            leaveTimeoutRef.current = null
          }
        }}
        onMouseLeave={handleMouseLeave}
      >
        {content}
      </PopoverContent>
    </Popover>
  )
})
HoverPopover.displayName = "HoverPopover"

export { Popover, PopoverAnchor, PopoverTrigger, PopoverContent, HoverPopover }
